import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { projektordnerName } from '@schnitt/shared';
import { api } from '../api';
import { useEditor } from '../store';
import { Sheet } from './Sheet';

/**
 * Einen Ordner der Ablage aussuchen (7.50).
 *
 * Sascha, 7. September: „Bei nextcloud müssen wir dann einen Ordner verbinden
 * können.“ Ausgesucht wird durch Blättern, nicht durch Tippen: einen Pfad
 * abzuschreiben ist die Gelegenheit, sich zu vertippen — und der Fehler zeigt
 * sich dann erst beim ersten Hochladen.
 *
 * Gezeigt werden nur Ordner. Die Dateien darin stehen als Zahl daneben, damit
 * man den richtigen Ordner *erkennt* („der mit den 42 Dateien“), ohne sie
 * einzeln durchgehen zu müssen.
 */
export function AblageOrdnerSheet({
  projectId,
  onClose,
  onFertig,
}: {
  projectId: string;
  onClose: () => void;
  onFertig: () => void;
}): ReactElement {
  const toast = useEditor((s) => s.toast);
  // So heisst der neue Ordner gleich, mit dem Tag vorn (7.232).
  const neuerName = useEditor((s) => projektordnerName(s.project?.name ?? 'Projekt', s.project?.createdAt ?? new Date()));
  const [stelle, setStelle] = useState<string | undefined>(undefined);
  const [stand, setStand] = useState<{
    anbieter: string;
    id: string;
    wurzel: string;
    ordner: { id: string; name: string }[];
    dateien: number;
  } | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  const laden = useCallback(async (id?: string) => {
    setFehler(null);
    try {
      setStand(await api.ablageOrdner(id));
    } catch (err) {
      setStand(null);
      setFehler(err instanceof Error ? err.message : 'Die Ablage antwortet nicht.');
    }
  }, []);
  useEffect(() => {
    void laden(stelle);
  }, [laden, stelle]);

  /** Ein Schritt zurück: beim Pfad das letzte Stück weg, bei Box gibt es das nicht. */
  const hoeher = stand && stand.id !== stand.wurzel && stand.id.includes('/') ? stand.id.replace(/\/[^/]+$/, '') : null;
  const zurueckMoeglich = Boolean(stand && stand.id !== stand.wurzel);

  const verbinden = async () => {
    if (!stand) return;
    setLaeuft(true);
    try {
      const { ablage } = await api.ablageVerbinden(projectId, stand.id);
      toast('success', `Verbunden mit „${ablage.name || 'dem Ordner'}“ — „rohdaten“ liegt darin.`);
      onFertig();
    } catch (err) {
      toast('error', err instanceof Error ? err.message : 'Verbinden ging nicht.');
    } finally {
      setLaeuft(false);
    }
  };

  // Ein neuer Ordner für dieses Projekt, dort, wo man gerade steht (7.223) --
  // im Hauptordner der übliche Weg, und der, den man dort sucht.
  const neuAnlegen = async () => {
    if (!stand) return;
    setLaeuft(true);
    try {
      const { ablage } = await api.ablageNeu(projectId, stand.id);
      toast('success', `Ordner „${ablage.name}“ angelegt und verbunden — „rohdaten“ liegt darin.`);
      onFertig();
    } catch (err) {
      toast('error', err instanceof Error ? err.message : 'Anlegen ging nicht.');
    } finally {
      setLaeuft(false);
    }
  };
  /** Der Hauptordner für alle Projekte ist selbst kein Projektordner (7.223). */
  const imHauptordner = Boolean(stand && stand.wurzel && stand.id === stand.wurzel);
  const ordnerName = stand?.id.split('/').filter(Boolean).at(-1) ?? '';

  return (
    <Sheet title="Ordner in der Ablage" onClose={onClose}>
      {fehler ? (
        <p className="hint" data-ablage-fehler>
          {fehler} Die Zugangsdaten stehen unter <em>Verwaltung → Ablage</em>.
        </p>
      ) : null}
      {stand ? (
        <>
          <p className="hint" data-ablage-stelle style={{ marginTop: 0 }}>
            {stand.anbieter === 'nextcloud' ? 'Nextcloud' : 'Box'} · {stand.id || '/'}
            {stand.dateien > 0 ? ` · ${stand.dateien} Datei${stand.dateien === 1 ? '' : 'en'} darin` : ''}
          </p>
          <div className="list">
            {zurueckMoeglich ? (
              <button className="row" style={{ textAlign: 'left' }} onClick={() => setStelle(hoeher ?? stand.wurzel)}>
                ← Eine Ebene höher
              </button>
            ) : null}
            {stand.ordner.map((o) => (
              <button
                key={o.id}
                className="row"
                data-ablage-ordner={o.id}
                style={{ textAlign: 'left' }}
                onClick={() => setStelle(o.id)}
              >
                {o.name}
              </button>
            ))}
            {stand.ordner.length === 0 ? <p className="hint">Hier liegen keine Unterordner.</p> : null}
          </div>
          {stand.anbieter === 'nextcloud' ? (
            <button
              className={imHauptordner || !stand.id ? 'primary' : ''}
              style={{ marginTop: 14 }}
              onClick={() => void neuAnlegen()}
              disabled={laeuft}
              data-ablage-neu
            >
              {laeuft ? 'Lege an…' : `Neuer Ordner „${neuerName}“ hier`}
            </button>
          ) : null}
          {/* Ganz oben gibt es nichts zu verbinden: der Pfad wäre leer, und
              ein leerer Ordner heisst am Projekt „nicht verbunden“. Und der
              Hauptordner für alle Projekte ist keiner (7.223). Der Knopf
              nennt den Ordner beim Namen -- „Diesen Ordner“ las sich wie
              „den, den ich gleich antippe“. */}
          <button
            className={imHauptordner || !stand.id ? '' : 'primary'}
            style={{ marginTop: 10 }}
            onClick={() => void verbinden()}
            disabled={laeuft || !stand.id || imHauptordner}
            data-ablage-verbinden
          >
            {laeuft
              ? 'Verbinde…'
              : imHauptordner
                ? 'Das ist der Hauptordner — darin einen Ordner öffnen'
                : stand.id
                  ? `Mit „${ordnerName}“ verbinden`
                  : 'Erst einen Ordner öffnen'}
          </button>
          <p className="hint">
            Das Projekt bekommt darin einen Unterordner <em>rohdaten</em> für das Material; der Schnitt selbst
            liegt als Datei daneben.
          </p>
        </>
      ) : fehler ? null : (
        <p className="hint">Lädt…</p>
      )}
    </Sheet>
  );
}
